import { Box, RefreshCw } from 'lucide-react';

import type { AvatarMesh } from '@/services/avatarApi';
import { cn } from '@/lib/cn';
import avatar3dCopy from '@/i18n/copy/avatar-3d.json';

type CtaKind =
  | { kind: 'hidden' }
  | { kind: 'make' }
  | { kind: 'inFlight'; copyKey: keyof typeof avatar3dCopy }
  | { kind: 'regenerate' }
  | { kind: 'retry' }
  | { kind: 'blocked' };

function decide(mesh: AvatarMesh | undefined, hasAlpha: boolean): CtaKind {
  if (!hasAlpha) return { kind: 'hidden' };
  if (!mesh || mesh.status === 'none') return { kind: 'make' };
  switch (mesh.status) {
    case 'queued':
    case 'claimed':
    case 'meshing':
      return { kind: 'inFlight', copyKey: 'avatar3d.cta.inProgress' };
    case 'validating':
      return { kind: 'inFlight', copyKey: 'avatar3d.cta.validating' };
    case 'uploading':
      return { kind: 'inFlight', copyKey: 'avatar3d.cta.uploading' };
    case 'ready':
      return { kind: 'regenerate' };
    case 'failed':
      return mesh.attempts >= 3 ? { kind: 'blocked' } : { kind: 'retry' };
    // Phase 2 animation states are handled by AnimateMeCta, not Make3dCta.
    case 'animating_queued':
    case 'animating_rigging':
    case 'animating_retargeting':
    case 'animating_merging':
    case 'animating_uploading':
    case 'animated_ready':
    case 'animated_failed':
      return { kind: 'hidden' };
  }
}

interface Make3dCtaProps {
  mesh: AvatarMesh | undefined;
  hasAlpha: boolean;
  isPending: boolean;
  onTrigger: (force: boolean) => void | Promise<void>;
}

export function Make3dCta({ mesh, hasAlpha, isPending, onTrigger }: Make3dCtaProps) {
  const cta = decide(mesh, hasAlpha);
  if (cta.kind === 'hidden') return null;

  const baseClass =
    'flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-opacity active:opacity-80';

  if (cta.kind === 'make') {
    return (
      <button
        type="button"
        onClick={() => onTrigger(false)}
        disabled={isPending}
        className={cn(baseClass, 'bg-bg-secondary text-fg', isPending && 'opacity-60')}
      >
        <Box aria-hidden className="h-4 w-4" />
        {avatar3dCopy['avatar3d.cta.make']}
      </button>
    );
  }

  if (cta.kind === 'regenerate') {
    return (
      <button
        type="button"
        onClick={() => onTrigger(true)}
        disabled={isPending}
        className={cn(baseClass, 'bg-brand/15 text-brand', isPending && 'opacity-60')}
      >
        <RefreshCw aria-hidden className="h-4 w-4" />
        {avatar3dCopy['avatar3d.cta.regenerate']}
      </button>
    );
  }

  if (cta.kind === 'retry') {
    return (
      <button
        type="button"
        onClick={() => onTrigger(false)}
        disabled={isPending}
        className={cn(
          baseClass,
          'bg-red-500/15 text-red-400',
          isPending && 'opacity-60',
        )}
      >
        <RefreshCw aria-hidden className="h-4 w-4" />
        {avatar3dCopy['avatar3d.cta.retry']}
      </button>
    );
  }

  if (cta.kind === 'blocked') {
    return (
      <div
        className={cn(
          baseClass,
          'cursor-not-allowed bg-bg-secondary text-fg-muted opacity-60',
        )}
      >
        {avatar3dCopy['avatar3d.cta.blocked']}
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled
      className={cn(baseClass, 'bg-bg-secondary text-fg opacity-60')}
    >
      <RefreshCw aria-hidden className="h-4 w-4 animate-spin" />
      {avatar3dCopy[cta.copyKey]}
    </button>
  );
}
